import * as React from 'react';
import { useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { PassengerState } from '../../Checkout/store/passengers/reducers';
import {
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment,
	SegmentFragment
} from '@websky/graphql';
import { AvailableSegment, AvailableSegmentPassenger, ServicePassengerSelected } from '../types';
import { Switch } from '@shared/ui';
import { useTheme } from '../../theme';
import SimpleLoader from '../../SimpleLoader';
import { SelectedServicesState, SelectedServiceType } from '../../Checkout/store/selectedServices/reducers';
import {
	computeServicesMap,
	getAvailableSegments,
	getIsDifferentServicesOrPassengers,
	getIsServiceCanModify,
	getNewSelectedServices
} from '../utils';
import Control from './Control/Control';
import { useToggleable } from '../../hooks';
import { useGetGdsServicesCalculatedCountInfo } from '@shared/lib/gdsService/useGetGdsServicesCalculatedCountInfo';
import SegmentCollapse from '../../AdditionalServiceWrapper/components/SegmentCollapse/SegmentCollapse';
import Money from '../../Money';
import { useVipServiceState } from '../hooks';
import WebskyAnalyticsApollo from '../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { Props as PassengerPriceControlProps } from '../../CheckoutAdditionalService/components/PassengerPriceControl/PassengerPriceControl';
import { WxEvents } from '../../DataLayerAnalytics/types';
import { default as BaseButton } from '../../BaseComponents/Button';
import { reactElementAdapter } from '@shared/react';

type OrderServiceFragment =
	| GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
	| GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment;

export interface Renderers {
	/**
	 * @deprecated use the showControlPrice prop instead override
	 */
	renderPassenger: (props: Omit<PassengerPriceControlProps, 'price'>) => React.ReactNode;
}

export interface Props extends Partial<Renderers> {
	className?: string;
	headerClassName?: string;
	passengers: PassengerState[];
	services: OrderServiceFragment[];
	header?: React.ReactNode;
	segments: SegmentFragment[];
	onSave?: (selectedServices: ServicePassengerSelected[]) => void;
	loading?: boolean;
	showTotalPrice?: boolean;
	showControlPrice?: boolean;
	selectedServices?: SelectedServicesState;
	onClose?: () => void;
	subheader?: React.ReactElement | string;
	type?: 'switch' | 'checkbox' | 'counter';
	readOnly?: boolean;
	disableOnSameState?: boolean;
}

const VipServiceSelect: React.FC<Props> = ({
	className,
	headerClassName,
	passengers,
	services,
	segments,
	header,
	loading,
	showTotalPrice,
	showControlPrice,
	onSave,
	selectedServices,
	onClose,
	subheader,
	type = 'switch',
	renderPassenger,
	readOnly,
	disableOnSameState
}) => {
	const { t } = useTranslation('VipServiceSelect');
	const { VipServiceSelect: theme } = useTheme('VipServiceSelect');

	const servicesMap = useMemo(() => {
		return new Map(services.map(service => [service.id, service]));
	}, [services]);

	const { state, totalPrice, isDifferentState, dispatch } = useVipServiceState(
		passengers,
		services,
		selectedServices
	);

	const isCheckboxOrSwitch = type === 'checkbox' || type === 'switch';

	const servicesStateMap = useMemo(() => {
		return computeServicesMap(services, selectedServices, isCheckboxOrSwitch);
	}, [services, selectedServices, isCheckboxOrSwitch]);

	const { availableSegments, isDifferentServicesOrPassengers } = useMemo(() => {
		return {
			availableSegments: getAvailableSegments(segments, passengers, servicesStateMap),
			isDifferentServicesOrPassengers: getIsDifferentServicesOrPassengers(servicesStateMap)
		};
	}, [segments, passengers, servicesStateMap]);

	const subheaderElement = useMemo(() => reactElementAdapter(subheader), [subheader]);

	const { isOpen: differentSegment, toggle: toggleDifferentSegment } = useToggleable(
		availableSegments.length > 1 || isDifferentServicesOrPassengers
	);

	const getGdsServicesCalculatedCountInfo = useGetGdsServicesCalculatedCountInfo();

	const resetAllPassengersState = useCallback(() => {
		passengers.forEach(passenger => {
			availableSegments.forEach(segment => {
				dispatch({
					count: 0,
					passengerId: passenger.id,
					segmentId: segment.segmentId
				});
			});
		});
	}, [passengers, availableSegments, dispatch]);

	const differentSegmentToggle = useCallback(() => {
		if (differentSegment) {
			resetAllPassengersState();
		}

		toggleDifferentSegment();
	}, [differentSegment, resetAllPassengersState, toggleDifferentSegment]);

	const onSaveHandler = useCallback(() => {
		if (isDifferentState) {
			const newServices = getNewSelectedServices(servicesStateMap, state, !differentSegment);
			onSave?.(newServices);
		} else {
			onClose?.();
		}
	}, [isDifferentState, servicesStateMap, state, differentSegment, onSave, onClose]);

	useEffect(() => {
		if (services?.length > 0) {
			const service = services[0];
			const subType = 'subType' in service ? service.subType : undefined;

			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.VipList, { subType });
		}
	}, []);

	const onSetCount = useCallback(
		(passenger: AvailableSegmentPassenger) => (count: number) => {
			const segmentIds = differentSegment ? [passenger.segmentId] : availableSegments.map(s => s.segmentId);

			segmentIds.forEach(segmentId => {
				dispatch({
					count,
					segmentId,
					passengerId: passenger.passengerId
				});
			});
		},
		[differentSegment, availableSegments, dispatch]
	);

	const getPassengerServiceState = useCallback(
		({ serviceId, segmentId, passengerId }: { serviceId: string; segmentId: string; passengerId: string }) => {
			const gdsService = servicesMap.get(serviceId);

			const count = state[segmentId]?.[passengerId] ?? 0;
			const confirmedCount = (selectedServices?.[passengerId]?.[serviceId]?.find(s =>
				s?.segments.includes(segmentId)
			)?.servicePayload as SelectedServiceType)?.confirmedCount;

			const isConfirmed = confirmedCount > 0 && count === confirmedCount;
			const isCanModify = getIsServiceCanModify({
				service: gdsService,
				services,
				segmentId,
				passengerId,
				count,
				confirmedCount,
				isCheckboxOrSwitch
			});

			return { gdsService, count, confirmedCount, isConfirmed, isCanModify };
		},
		[state, selectedServices, servicesMap, services, isCheckboxOrSwitch]
	);

	const onSetSegmentCount = useCallback(
		(segment: AvailableSegment, checked: boolean) => {
			segment.passengers.forEach(passenger => {
				dispatch({
					count: checked ? 1 : 0,
					passengerId: passenger.passengerId,
					segmentId: segment.segmentId
				});
			});
		},
		[dispatch]
	);

	const getSelectedServicesCount = useCallback(
		(passengers: AvailableSegmentPassenger[]) => {
			return passengers.reduce((total, passenger) => {
				return total + (state[passenger.segmentId]?.[passenger.passengerId] || 0);
			}, 0);
		},
		[state]
	);

	const getPassengerData = useCallback(
		(passenger: AvailableSegmentPassenger) => {
			const { gdsService, count, isConfirmed, isCanModify } = getPassengerServiceState(passenger);

			let maxCount: number | undefined;
			if (type === 'counter' && gdsService) {
				maxCount = getGdsServicesCalculatedCountInfo(passenger.passengerId, passenger.segmentId, {
					rfisc: gdsService.rfisc,
					subType: 'subType' in gdsService ? gdsService.subType : null,
					type: gdsService.type
				})?.maxCount;
			}

			return { count, isConfirmed, isCanModify, maxCount };
		},
		[type, getGdsServicesCalculatedCountInfo, getPassengerServiceState]
	);

	const renderPassengerHandler = useCallback(
		(passenger: AvailableSegmentPassenger) => {
			const { count, isConfirmed, isCanModify, maxCount } = getPassengerData(passenger);

			let servicePrice = showControlPrice ? servicesMap.get(passenger.serviceId)?.price : null;
			if (servicePrice && !differentSegment) {
				servicePrice = {
					...servicePrice,
					amount: servicePrice.amount * availableSegments.length
				};
			}

			if (renderPassenger) {
				return renderPassenger({ passenger, type, count, isConfirmed, onSetCount });
			}

			return (
				<Control
					type={type}
					count={count}
					price={servicePrice}
					disabled={isConfirmed || !isCanModify}
					label={
						<div className={theme.passenger}>
							{type !== 'checkbox' && (
								<div className={theme.number}>{parseInt(passenger.passengerId) + 1}</div>
							)}
							{passenger.name}
						</div>
					}
					maxCount={maxCount}
					onSetCount={onSetCount(passenger)}
				/>
			);
		},
		[
			getPassengerData,
			servicesMap,
			showControlPrice,
			renderPassenger,
			type,
			differentSegment,
			availableSegments,
			onSetCount
		]
	);

	const getDirectionName = useCallback((segments: AvailableSegment[]) => {
		return `${segments[0].departure} – ${segments[segments.length - 1].arrival}`;
	}, []);

	const segmentsToRender = useMemo(() => (differentSegment ? availableSegments : availableSegments.slice(0, 1)), [
		differentSegment,
		availableSegments
	]);

	const showFooterAdditionalControls = useMemo(
		() => availableSegments.length > 1 || !!(showTotalPrice && totalPrice),
		[availableSegments.length, showTotalPrice, totalPrice]
	);

	return (
		<div className={cn(theme.container, className)}>
			{loading && <SimpleLoader />}

			{header && <div className={cn(theme.header, headerClassName)}>{header}</div>}
			{subheaderElement && <div className={theme.subheader}>{subheaderElement}</div>}

			<div className={theme.content}>
				{segmentsToRender.map(segment => {
					const selectedCount = getSelectedServicesCount(segment.passengers);
					const showCheckbox =
						type === 'checkbox' &&
						segment.passengers.every(passenger => getPassengerServiceState(passenger).isCanModify);

					return (
						<SegmentCollapse
							key={segment.segmentId}
							className={theme.segment}
							date={segment.date}
							count={selectedCount}
							isSelected={segment.passengers.length === selectedCount}
							info={getDirectionName(differentSegment ? [segment] : availableSegments)}
							onChangeCheckbox={showCheckbox ? checked => onSetSegmentCount(segment, checked) : null}
						>
							{segment.passengers.map(passenger => (
								<React.Fragment key={passenger.passengerId}>
									{renderPassengerHandler(passenger)}
								</React.Fragment>
							))}
						</SegmentCollapse>
					);
				})}
			</div>

			<div className={theme.footer}>
				{showFooterAdditionalControls && (
					<div className={theme.controls}>
						{availableSegments.length > 1 && (
							<div className={theme.switch__wrapper}>
								<Switch
									classes={{
										label: theme.vipServiceSelect__switch_label
									}}
									checked={differentSegment}
									onChange={differentSegmentToggle}
									label={t('Different for other flight?')}
									disabled={isDifferentServicesOrPassengers}
								/>
							</div>
						)}

						{showTotalPrice && totalPrice && (
							<div className={theme.total}>
								{t('Total')}:
								<Money money={totalPrice} />
							</div>
						)}
					</div>
				)}

				<div className={theme.controls}>
					<BaseButton
						variant="smallSecondary"
						className={theme.cancel}
						data-test-id="vip-service-cancel"
						onClick={onClose}
					>
						{t('Cancel')}
					</BaseButton>

					{!readOnly && (
						<BaseButton
							onClick={onSaveHandler}
							variant="smallPrimary"
							className={theme.confirm}
							data-test-id="vip-service-confirm"
							disabled={disableOnSameState && !isDifferentState}
						>
							{t('Confirm')}
						</BaseButton>
					)}
				</div>
			</div>
		</div>
	);
};

export default React.memo(VipServiceSelect);
